import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { withTimeout } from '@/lib/withTimeout';
import type { Attachment, GroupColor, Note, NoteGroup } from '../types';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import GroupList, { type GroupFilter } from './notes/GroupList';
import GroupEditorModal from './notes/GroupEditorModal';
import NoteCard from './notes/NoteCard';
import NoteEditorModal, { type NotePayload } from './notes/NoteEditorModal';
import NoteViewModal from './notes/NoteViewModal';
import { datetimeLocalToIso, isArchiveGroup, sortGroupsArchiveLast } from './notes/noteUtils';

export default function NotesPanel() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [groups, setGroups] = useState<NoteGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<GroupFilter>('all');
  const [filterOpen, setFilterOpen] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Note | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [groupEditorOpen, setGroupEditorOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<NoteGroup | null>(null);
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupError, setGroupError] = useState('');

  const [deletingNote, setDeletingNote] = useState<Note | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<NoteGroup | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [archivingNote, setArchivingNote] = useState<Note | null>(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [viewing, setViewing] = useState<Note | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<string, string[]>>({});

  // Tải signed URL cho ảnh minh họa (chạy nền, có timeout, không chặn hiển thị danh sách)
  const loadImageUrls = async (notesData: Note[]) => {
    const urls: Record<string, string[]> = {};
    await Promise.all(
      notesData
        .filter((n) => n.image_urls?.length)
        .map(async (n) => {
          try {
            const results = await Promise.all(
              n.image_urls.map((p) =>
                withTimeout(
                  supabase.storage
                    .from('noteapp_images')
                    .createSignedUrl(p, 3600),
                  8000
                )
              )
            );
            urls[n.id] = results
              .map((r) => r.data?.signedUrl)
              .filter((u): u is string => !!u);
          } catch {
            // Bỏ qua ảnh không tải được
          }
        })
    );
    setImageUrls(urls);
  };

  const fetchAll = useCallback(async (retryCount = 0) => {
    if (retryCount === 0) {
      setLoading(true);
      setError('');
    }
    try {
      const [groupsRes, notesRes] = await withTimeout(
        Promise.all([
          supabase
            .from('noteapp_note_groups')
            .select('*')
            .order('created_at', { ascending: true }),
          supabase.from('noteapp_notes').select('*').order('updated_at', {
            ascending: false,
          }),
        ]),
        30000
      );
      if (groupsRes.error) throw groupsRes.error;
      if (notesRes.error) throw notesRes.error;
      setGroups(sortGroupsArchiveLast(groupsRes.data ?? []));
      const notesData = notesRes.data ?? [];
      setNotes(notesData);
      void loadImageUrls(notesData);
      setLoading(false);
    } catch (err) {
      console.error('fetchAll error:', err);
      if (retryCount < 2) {
        setTimeout(() => fetchAll(retryCount + 1), 3000);
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Không thể tải dữ liệu ghi chú. ${msg}`);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    // Kích hoạt kiểm tra cảnh báo Telegram (im lặng, không chặn UI)
    supabase.functions.invoke('noteapp-notify-expiring-notes').catch(() => {});
  }, [fetchAll]);

  const groupMap = new Map(groups.map((g) => [g.id, g]));
  const counts: Record<string, number> = {};
  notes.forEach((n) => {
    if (n.group_id) counts[n.group_id] = (counts[n.group_id] ?? 0) + 1;
  });
  const ungroupedCount = notes.filter((n) => !n.group_id).length;

  const filtered = notes.filter((n) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      n.title.toLowerCase().includes(q) ||
      n.content.toLowerCase().includes(q);
    if (!matchSearch) return false;
    if (activeFilter === 'all') return true;
    if (activeFilter === 'ungrouped') return !n.group_id;
    return n.group_id === activeFilter;
  });

  const openCreate = () => {
    setEditing(null);
    setFormError('');
    setEditorOpen(true);
  };

  const openEdit = (note: Note) => {
    setEditing(note);
    setFormError('');
    setEditorOpen(true);
  };

  const handleSaveNote = async (payload: NotePayload) => {
    if (!payload.title) {
      setFormError('Vui lòng nhập tiêu đề.');
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const data: {
        title: string;
        content: string;
        group_id: string | null;
        expires_at: string | null;
        telegram_alert: boolean;
        alert_sent: boolean;
        image_urls: string[];
        attachments: Attachment[];
      } = {
        title: payload.title,
        content: payload.content,
        group_id: payload.groupId,
        expires_at: payload.expiresAt
          ? datetimeLocalToIso(payload.expiresAt)
          : null,
        telegram_alert: payload.telegramAlert,
        alert_sent: false,
        image_urls: payload.existingImageUrls,
        attachments: payload.existingAttachments,
      };

      // Tải các ảnh minh họa mới lên
      const uploadedPaths: string[] = [];
      if (payload.imageFiles.length) {
        const { data: authData } = await supabase.auth.getUser();
        const userId = authData.user?.id ?? 'unknown';
        for (const file of payload.imageFiles) {
          const rawExt = (file.name.split('.').pop() ?? 'png')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '');
          const safeExt = rawExt || 'png';
          const path = `${userId}/${Date.now()}-${crypto.randomUUID()}.${safeExt}`;
          const { error: uploadErr } = await supabase.storage
            .from('noteapp_images')
            .upload(path, file, { cacheControl: '3600', upsert: false });
          if (uploadErr) throw uploadErr;
          uploadedPaths.push(path);
        }
      }
      data.image_urls = [...payload.existingImageUrls, ...uploadedPaths];

      // Tải các file đính kèm mới lên
      const uploadedAttachments: Attachment[] = [];
      if (payload.attachmentFiles.length) {
        const { data: authData } = await supabase.auth.getUser();
        const userId = authData.user?.id ?? 'unknown';
        for (const file of payload.attachmentFiles) {
          const rawExt = (file.name.split('.').pop() ?? '')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '');
          const safeExt = rawExt ? `.${rawExt}` : '';
          const path = `${userId}/files/${Date.now()}-${crypto.randomUUID()}${safeExt}`;
          const { error: uploadErr } = await supabase.storage
            .from('noteapp_attachments')
            .upload(path, file, { cacheControl: '3600', upsert: false });
          if (uploadErr) throw uploadErr;
          uploadedAttachments.push({
            path,
            name: file.name,
            size: file.size,
            type: file.type,
          });
        }
      }
      data.attachments = [...payload.existingAttachments, ...uploadedAttachments];

      // Xóa ảnh cũ bị gỡ bỏ khỏi storage
      if (editing?.image_urls?.length) {
        const removed = editing.image_urls.filter(
          (p) => !payload.existingImageUrls.includes(p)
        );
        if (removed.length) {
          await supabase.storage.from('noteapp_images').remove(removed);
        }
      }

      // Xóa file đính kèm cũ bị gỡ bỏ khỏi storage
      if (editing?.attachments?.length) {
        const removedPaths = editing.attachments
          .filter(
            (a) =>
              !payload.existingAttachments.some((k) => k.path === a.path)
          )
          .map((a) => a.path);
        if (removedPaths.length) {
          await supabase.storage.from('noteapp_attachments').remove(removedPaths);
        }
      }

      if (editing) {
        const { error: err } = await supabase
          .from('noteapp_notes')
          .update(data)
          .eq('id', editing.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('noteapp_notes').insert(data);
        if (err) throw err;
      }
      setEditorOpen(false);
      await fetchAll();
    } catch {
      setFormError('Không thể lưu ghi chú. Vui lòng thử lại.');
    } finally {
      setSaving(false);
    }
  };

  const openAddGroup = () => {
    setEditingGroup(null);
    setGroupError('');
    setGroupEditorOpen(true);
  };

  const openEditGroup = (group: NoteGroup) => {
    setEditingGroup(group);
    setGroupError('');
    setGroupEditorOpen(true);
  };

  const handleSaveGroup = async (name: string, color: GroupColor) => {
    setGroupError('');
    setGroupSaving(true);
    try {
      if (editingGroup) {
        const { error: err } = await supabase
          .from('noteapp_note_groups')
          .update({ name, color })
          .eq('id', editingGroup.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase
          .from('noteapp_note_groups')
          .insert({ name, color });
        if (err) throw err;
      }
      setGroupEditorOpen(false);
      await fetchAll();
    } catch {
      setGroupError('Không thể lưu nhóm. Vui lòng thử lại.');
    } finally {
      setGroupSaving(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!deletingNote) return;
    setDeleteLoading(true);
    try {
      const { error: err } = await supabase
        .from('noteapp_notes')
        .delete()
        .eq('id', deletingNote.id);
      if (err) throw err;

      // Xóa sạch ảnh minh họa và file đính kèm khỏi server
      // (best-effort, không chặn việc xóa ghi chú nếu xóa file gặp lỗi)
      const storageTasks: Promise<unknown>[] = [];
      if (deletingNote.image_urls?.length) {
        storageTasks.push(
          supabase.storage.from('noteapp_images').remove(deletingNote.image_urls)
        );
      }
      if (deletingNote.attachments?.length) {
        storageTasks.push(
          supabase.storage
            .from('noteapp_attachments')
            .remove(deletingNote.attachments.map((a) => a.path))
        );
      }
      await Promise.allSettled(storageTasks);

      setDeletingNote(null);
      await fetchAll();
    } catch {
      setError('Không thể xóa ghi chú. Vui lòng thử lại.');
      setDeletingNote(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!archivingNote) return;
    setArchiveLoading(true);
    setError('');
    try {
      let archiveGroup = groups.find((g) => isArchiveGroup(g.name));
      if (!archiveGroup) {
        const { data, error: createErr } = await supabase
          .from('noteapp_note_groups')
          .insert({ name: 'Lưu trữ', color: 'neutral' })
          .select()
          .single();
        if (createErr) throw createErr;
        archiveGroup = data;
      }
      const { error: err } = await supabase
        .from('noteapp_notes')
        .update({ group_id: archiveGroup.id })
        .eq('id', archivingNote.id);
      if (err) throw err;
      setArchivingNote(null);
      await fetchAll();
    } catch {
      setError('Không thể lưu trữ ghi chú. Vui lòng thử lại.');
      setArchivingNote(null);
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    setDeleteLoading(true);
    try {
      const { error: err } = await supabase
        .from('noteapp_note_groups')
        .delete()
        .eq('id', deletingGroup.id);
      if (err) throw err;
      if (activeFilter === deletingGroup.id) setActiveFilter('all');
      setDeletingGroup(null);
      await fetchAll();
    } catch {
      setDeletingGroup(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const defaultGroupId =
    activeFilter !== 'all' && activeFilter !== 'ungrouped'
      ? activeFilter
      : null;

  const activeFilterLabel =
    activeFilter === 'all'
      ? 'Tất cả'
      : activeFilter === 'ungrouped'
        ? 'Chưa phân nhóm'
        : (groups.find((g) => g.id === activeFilter)?.name ?? 'Tất cả');

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex flex-col gap-3 border-b border-background-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
        <div>
          <h2 className="font-heading text-lg font-semibold text-foreground-950 sm:text-xl">
            Ghi chú
          </h2>
          <p className="text-sm text-foreground-500">
            Sắp xếp ghi chú theo nhóm, đặt hạn và nhận cảnh báo.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm kiếm..."
              className="w-full rounded-lg border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-base text-foreground-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200 sm:w-56 sm:py-2 sm:text-sm"
            />
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3 text-base font-semibold text-background-50 transition hover:bg-primary-600 sm:w-auto sm:py-2 sm:text-sm"
          >
            <i className="ri-add-line text-lg" />
            Thêm ghi chú
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop group list */}
        <div className="hidden md:block">
          <GroupList
            groups={groups}
            counts={counts}
            ungroupedCount={ungroupedCount}
            activeFilter={activeFilter}
            onSelect={setActiveFilter}
            onAddGroup={openAddGroup}
            onEditGroup={openEditGroup}
            onDeleteGroup={setDeletingGroup}
          />
        </div>

        {/* Main content */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Mobile group filter dropdown */}
          <div className="relative flex items-center gap-2 border-b border-background-200 px-4 py-3 md:hidden">
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="flex flex-1 items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-medium text-foreground-900 transition hover:border-primary-400"
            >
              <span className="truncate">{activeFilterLabel}</span>
              <i
                className={`ri-arrow-down-s-line text-base text-foreground-500 transition-transform ${
                  filterOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            <button
              type="button"
              onClick={openAddGroup}
              className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border-2 border-dashed border-orange-400 px-3 py-2.5 text-sm font-medium text-orange-600 transition hover:border-orange-500 hover:bg-orange-50 hover:text-orange-700"
            >
              <i className="ri-add-line" />
              Nhóm
            </button>

            {filterOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setFilterOpen(false)}
                />
                <div className="absolute left-4 right-4 top-full z-20 mt-1 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveFilter('all');
                      setFilterOpen(false);
                    }}
                    className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-3 text-sm font-medium transition ${
                      activeFilter === 'all'
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    Tất cả
                    {activeFilter === 'all' && (
                      <i className="ri-check-line text-base" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveFilter('ungrouped');
                      setFilterOpen(false);
                    }}
                    className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-3 text-sm font-medium transition ${
                      activeFilter === 'ungrouped'
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    Chưa phân nhóm
                    {activeFilter === 'ungrouped' && (
                      <i className="ri-check-line text-base" />
                    )}
                  </button>
                  {groups.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => {
                        setActiveFilter(g.id);
                        setFilterOpen(false);
                      }}
                      className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-3 text-sm font-medium transition ${
                        activeFilter === g.id
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {g.name}
                      {activeFilter === g.id && (
                        <i className="ri-check-line text-base" />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Notes list */}
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20">
                <i className="ri-error-warning-line text-3xl text-accent-500" />
                <p className="text-sm text-foreground-500">{error}</p>
                <button
                  type="button"
                  onClick={fetchAll}
                  className="whitespace-nowrap rounded-lg border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition hover:bg-background-100"
                >
                  Thử lại
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-sticky-note-line text-2xl" />
                </div>
                <p className="text-sm text-foreground-500">
                  {notes.length === 0
                    ? 'Chưa có ghi chú nào. Hãy tạo ghi chú đầu tiên.'
                    : 'Không tìm thấy ghi chú phù hợp.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3">
                {filtered.map((note) => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    group={note.group_id ? groupMap.get(note.group_id) : undefined}
                    onView={setViewing}
                    onEdit={openEdit}
                    onDelete={setDeletingNote}
                    onArchive={setArchivingNote}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Note editor */}
      <NoteEditorModal
        open={editorOpen}
        editing={editing}
        groups={groups}
        defaultGroupId={defaultGroupId}
        saving={saving}
        error={formError}
        onClose={() => setEditorOpen(false)}
        onSave={handleSaveNote}
      />

      {/* Note view detail */}
      <NoteViewModal
        note={viewing}
        group={viewing?.group_id ? groupMap.get(viewing.group_id) : undefined}
        imageUrls={viewing ? imageUrls[viewing.id] : undefined}
        onClose={() => setViewing(null)}
        onEdit={(note) => {
          setViewing(null);
          openEdit(note);
        }}
      />

      {/* Group editor */}
      <GroupEditorModal
        open={groupEditorOpen}
        editing={editingGroup}
        saving={groupSaving}
        error={groupError}
        onClose={() => setGroupEditorOpen(false)}
        onSave={handleSaveGroup}
      />

      {/* Delete note confirm */}
      <ConfirmDialog
        open={!!deletingNote}
        title="Xóa ghi chú"
        message={`Bạn có chắc muốn xóa ghi chú "${deletingNote?.title}"? Hành động này không thể hoàn tác.`}
        loading={deleteLoading}
        onCancel={() => setDeletingNote(null)}
        onConfirm={handleDeleteNote}
      />

      {/* Archive note confirm */}
      <ConfirmDialog
        open={!!archivingNote}
        title="Lưu trữ ghi chú"
        message={`Bạn có chắc muốn chuyển ghi chú "${archivingNote?.title}" vào nhóm Lưu trữ? Ghi chú sẽ được ẩn khỏi danh sách chính nhưng vẫn có thể truy cập lại sau này.`}
        confirmLabel="Lưu trữ"
        loading={archiveLoading}
        onCancel={() => setArchivingNote(null)}
        onConfirm={handleArchive}
      />

      {/* Delete group confirm */}
      <ConfirmDialog
        open={!!deletingGroup}
        title="Xóa nhóm"
        message={`Bạn có chắc muốn xóa nhóm "${deletingGroup?.name}"? Các ghi chú trong nhóm sẽ chuyển về "Chưa phân nhóm".`}
        loading={deleteLoading}
        onCancel={() => setDeletingGroup(null)}
        onConfirm={handleDeleteGroup}
      />
    </div>
  );
}